import React, { useMemo } from "react";
import classNames from "classnames";
import classes from "./FieldValue.scss";
import MediaPlaceholder from "UIComponents/MediaPlaceholder";
import { colors } from "Constants";
import { MediaCard } from "@toddle-edu/ds-web";
import moment from "moment";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { FIELD_TYPE_ENUM } from "Login/routes/Administrator/commonComponents/RoutineAndMealTemplates/routes/RoutineTemplates/components/CreateRoutineTemplate/CreateRoutineTemplate";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

const styles = {
  infoTooltipStyle: {
    width: "280px",
  },
  sectionHeaderTopStyle: { paddingTop: "16px" },
};

const BLANK_OBJECT = {};

const PreviewAttachments = props => {
  const { attachments, readValue = false } = props;
  const commonMediaPlaceholderProps = {
    showOverlayIcons: false,
    showMiddleContainerIcons: false,
    showIconCardsWithExtension: true,
    showExtensionOnCornerByHover: false,
  };
  const mediaPlaceholderProps = {
    ...commonMediaPlaceholderProps,
    imageMaxWidth: 200,
    imageMaxHeight: 112,
    containerStyle: {
      overflow: "visible",
      width: "200px",
      height: "112px",
    },
    itemStyles: {
      border: `1px solid ${colors.borderSubtle}`,
      borderRadius: "8px",
      width: "200px",
      height: "112px",
    },
    fileIconSize: "xx-small",
    extensionTextStyle: { fontSize: "1.2rem", marginTop: "2px" },
  };

  return (
    <div className={classes.attachmentContainer}>
      {_.map(attachments, (attachment, index) => {
        return (
          <div className={classes.attachmentItemContainer}>
            <MediaPlaceholder
              key={index}
              attachments={!readValue ? [attachment] : attachment.attachments}
              {...mediaPlaceholderProps}
              attachmentIndex={index}
              attachmentSize={_.size(attachments)}
            />
          </div>
        );
      })}
    </div>
  );
};

const style = {
  attachmentField: {
    paddingBottom: "8px",
  },
  timeDuration: {
    paddingLeft: "4px",
  },
};
const FieldValue = props => {
  const {
    isSidePaneContent,
    theatreActiveAttachmentIndex,
    readFromObject = false,
    studentRoutineData,
    fields,
    fieldValues,
    fieldsForPreview,
    onMediaClick,
    onTheatreAttachmentClick,
    fieldValueContainerStyle = {},
  } = props;

  const { t } = useTranslation(["journal", "common"]);

  const labelClass = classNames({
    ["text-body"]: true,
    [classes.label]: true,
  });

  const labelClassV2 = classNames({
    ["text-label-l"]: true,
    [classes.labelV2]: true,
  });

  const valueClass = classNames({
    ["text-body"]: true,
  });

  const firstTimeFieldContainerClass = classNames({
    [classes.firstTimeField]: true,
    [classes.fieldContainer]: true,
  });

  const secondFieldContainerClass = classNames({
    [classes.secondTimeField]: true,
    [classes.fieldContainer]: true,
  });

  const TimeRange = React.memo(({ field, studentRoutineData, readValue }) => {
    const isTimeRange = field.type === "TIME_RANGE";
    const firstTimeFieldContainerClass = classNames({
      [classes.firstTimeField]: isTimeRange,
      [classes.fieldContainer]: true,
    });

    const getTime = path => moment(_.get(studentRoutineData, path));

    const startTimePath = readValue
      ? "value.value.startTime"
      : `${field.id}.value.startTime`;
    const endTimePath = readValue
      ? "value.value.endTime"
      : `${field.id}.value.endTime`;

    const startTime = getTime(startTimePath);
    const endTime = getTime(endTimePath);

    const isTimeValuePresent =
      !_.isUndefined(_.get(studentRoutineData, "value.value")) ||
      !_.isUndefined(_.get(studentRoutineData, `${field.id}.value`));

    const timeValue = moment(
      _.get(studentRoutineData, readValue ? "value.value" : `${field.id}.value`)
    );

    //keeping this for reference
    // const getDuration = useMemo(() => {
    //   const duration = moment.duration(moment(endTime)?.diff?.(startTime));
    //   const hours = Math.floor(duration?.asHours());
    //   const minutes = duration?.minutes();
    //   return getTimeDurationString(hours, minutes);
    // }, [startTime, endTime]);

    const getDuration = useMemo(() => {
      const startTimeOfDay = moment()
        .hours(startTime.hours())
        .minutes(startTime.minutes());
      const endTimeOfDay = moment()
        .hours(endTime.hours())
        .minutes(endTime.minutes());

      let duration;
      if (endTimeOfDay.isBefore(startTimeOfDay)) {
        duration = moment.duration(
          endTimeOfDay.add(24, "hours").diff(startTimeOfDay)
        );
      } else {
        duration = moment.duration(endTimeOfDay.diff(startTimeOfDay));
      }

      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();

      // Format hours string if hours > 0
      const hoursStr = hours > 0 ? `${hours}h` : "";

      // Format minutes string if minutes > 0
      const minutesStr = minutes > 0 ? `${minutes}m` : "";

      // Add space between if both parts exist
      const spacer = hours > 0 && minutes > 0 ? " " : "";

      return hoursStr + spacer + minutesStr;
    }, [startTime, endTime]);

    const labelClass = classNames({
      [classes.duration]: true,
      ["text-body"]: true,
    });
    const autoCalculateTotalDuration =
      field?.metadata?.autoCalculateTotalDuration;
    return (
      <div>
        {" "}
        {!isTimeRange ? (
          <div className={classes.timeRangeContainer}>
            <div className={firstTimeFieldContainerClass}>
              <div className={labelClass}>
                {!isTimeRange
                  ? field.label
                  : field?.metadata?.startTimePlaceholder ?? ""}
              </div>
              <div className={valueClass}>
                {isTimeValuePresent
                  ? !isTimeRange
                    ? timeValue.format("hh:mm A")
                    : !_.isUndefined(_.get(studentRoutineData, startTimePath))
                    ? startTime.format("hh:mm A")
                    : "-"
                  : "-"}
              </div>
            </div>
            {isTimeRange ? (
              <div className={secondFieldContainerClass}>
                <div className={labelClass}>
                  {!isTimeRange
                    ? field.label
                    : field?.metadata?.endTimePlaceholder ?? ""}
                </div>
                <div className={valueClass}>
                  {isTimeValuePresent && _.get(studentRoutineData, endTimePath)
                    ? !_.isUndefined(_.get(studentRoutineData, endTimePath))
                      ? endTime.format("hh:mm A")
                      : "-"
                    : "-"}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        {isTimeRange ? (
          <div className={firstTimeFieldContainerClass}>
            <div className={labelClass}>
              {field.label ? field.label : "Time"}
            </div>
            <div className={valueClass}>
              {
                // write time in 12.15am to 12.30am format

                <span>
                  {!_.isUndefined(_.get(studentRoutineData, startTimePath)) &&
                  !_.isUndefined(_.get(studentRoutineData, endTimePath)) ? (
                    `${startTime?.format("hh.mm a")} ${t(
                      "common:to"
                    )} ${endTime?.format("hh.mm a")}`
                  ) : !_.isUndefined(
                      _.get(studentRoutineData, startTimePath)
                    ) ? (
                    <span> {startTime.format("hh.mm a")} </span>
                  ) : !_.isUndefined(_.get(studentRoutineData, endTimePath)) ? (
                    <span> {endTime?.format("hh.mm a")}</span>
                  ) : null}
                </span>
              }
              {isTimeRange &&
              autoCalculateTotalDuration &&
              isTimeValuePresent &&
              !_.isUndefined(_.get(studentRoutineData, startTimePath)) &&
              !_.isUndefined(_.get(studentRoutineData, endTimePath)) ? (
                <span style={style.timeDuration}>({getDuration})</span>
              ) : null}
            </div>
          </div>
        ) : null}
        {/* removing for dqc change keeping it for reference */}
        {/* {isTimeRange && autoCalculateTotalDuration && (
          <span className={classes.timeRangeContainer}>
            <span className={labelClass}>{t("journal:duration")}</span>
            <span className={labelClass}>
              {isTimeValuePresent ? getDuration : "-"}
            </span>
          </span>
        )} */}
      </div>
    );
  });

  TimeRange.displayName = "TimeRange";

  const getValueByDataKey = (
    keyValueMap,
    dataSets,
    selectedMealDate,
    startDate,
    endDate
  ) => {
    const mealMenuMap = Object.fromEntries(
      dataSets
        .filter(option => option.type === "MEAL_MENU")
        .flatMap(option => option.values)
        .map(valueType => [valueType.id, valueType?.item?.itemName])
    );

    const selectOptionMap = Object.fromEntries(
      _.map(
        _.find(dataSets, option => option.type === "SELECT_OPTION")?.values,
        valueType => [valueType.id, valueType.value]
      )
    );
    const selectedMealDateValue = moment(selectedMealDate).startOf("day");
    const mealStartDate = moment(
      startDate,
      DATE_TIME_CONSTANTS.YYYY_MM_DD
    ).startOf("day");
    const mealEndDate = moment(endDate, DATE_TIME_CONSTANTS.YYYY_MM_DD).endOf(
      "day"
    );
    const result = _.map(_.entries(keyValueMap), ([key, value]) => {
      return _.get(mealMenuMap, key) &&
        selectedMealDateValue.isSameOrAfter(mealStartDate) &&
        selectedMealDateValue.isSameOrBefore(mealEndDate) ? (
        <div className={valueClass} key={key}>
          {`${_.get(mealMenuMap, key)}-${_.get(selectOptionMap, value)}`}
        </div>
      ) : (
        "-"
      );
    });
    return result;
  };

  const attachmentFields = _.filter(fields, {
    type: "ATTACHMENT",
  });
  const attachmentValues = _.filter(fieldValues, item =>
    _.includes(_.map(attachmentFields, "id"), item.uid)
  );

  const getSelectedStudentAttachments = _.memoize(
    selectStudentAttachmentData => {
      let allAttachments = [];

      if (!_.isNil(selectStudentAttachmentData)) {
        const attachmentsGroups = _.get(
          selectStudentAttachmentData,
          "attachmentGroups",
          []
        );
        _.forEach(attachmentsGroups, group => {
          const attachments = _.get(group, "attachments", []);
          allAttachments = _.concat(allAttachments, attachments);
        });
      }
      return allAttachments;
    },
    selectStudentAttachmentData => {
      const attachmentGroups = _.get(
        selectStudentAttachmentData,
        "attachmentGroups",
        []
      );
      return _.map(
        attachmentGroups,
        group => `${group.id}_${_.map(group.attachments, "id").join(",")}`
      ).join("|");
    }
  );
  const updatedAttacments = [];
  _.forEach(attachmentValues, attachmentObj => {
    updatedAttacments.push(
      ...(getSelectedStudentAttachments(
        _.get(attachmentObj, "resolvedMinimalTree")
      ) || [])
    );
  });

  const getAttachmentIndex = url => {
    return updatedAttacments.findIndex(attachment => attachment.url === url);
  };

  return (
    <div className={classes.container} style={fieldValueContainerStyle}>
      {!readFromObject &&
        _.map(fields, (field, index) => {
          if (field.type === "MEAL") {
            const mealValues = _.filter(field.dataSets, item =>
              _.isEqual(item.type, "MEAL_MENU")
            );
            const valueObject = _.find(fieldValues, { uid: field.id });
            const startDate = _.find(mealValues, {
              id: valueObject?.value?.value?.mealMenuTemplateId,
            })?.startDate;
            const endDate = _.find(mealValues, {
              id: valueObject?.value?.value?.mealMenuTemplateId,
            })?.endDate;
            return (
              <div className={classes.fieldContainer}>
                <div className={labelClass}>
                  {field.label ??
                    _.find(mealValues, {
                      id: valueObject?.value?.value?.mealMenuTemplateId,
                    })?.title ??
                    "-"}
                </div>
                {getValueByDataKey(
                  valueObject?.value?.value?.options,
                  field.dataSets,
                  valueObject?.value?.value?.selectedMealDate,
                  startDate,
                  endDate
                )}
              </div>
            );
          }
          if (field.type === "TIME_RANGE" || field.type === "TIME") {
            return (
              <TimeRange
                field={field}
                studentRoutineData={_.find(fieldValues, { uid: field.id })}
                readValue={true}
              />
            );
          } else if (field.type === "ATTACHMENT") {
            const previewAttachment = _.find(fieldValues, { uid: field.id })
              ?.resolvedMinimalTree?.attachmentGroups;
            if (isSidePaneContent) {
              return (
                <div key={field.id}>
                  <div className={labelClass} style={style.attachmentField}>
                    {field?.label ?? t("common:attachment")}
                  </div>
                  {_.size(previewAttachment) ? (
                    <div className={classes.mediaGroup}>
                      {_.map(previewAttachment, (attachment, index) => {
                        const getAttachment = _.first(
                          _.get(attachment, "attachments")
                        );
                        const attachmentIndex = getAttachmentIndex(
                          _.get(getAttachment, "url", "")
                        );
                        return (
                          <div
                            tabIndex={0}
                            className={classes.mediaCardContainer}
                          >
                            <MediaCard
                              key={_.get(getAttachment, "id", "")}
                              cardType={"medium"}
                              name={_.get(getAttachment, "name", "")}
                              type={_.get(getAttachment, "type", "")}
                              size={_.get(getAttachment, "metadata.size", 0)}
                              mimeType={_.get(getAttachment, "mimeType")}
                              url={_.get(getAttachment, "url", "")}
                              thumbUrl={_.get(getAttachment, "thumbUrl", "")}
                              isActivated={
                                theatreActiveAttachmentIndex === attachmentIndex
                              }
                              showActionsOnHover={false}
                              onClick={onTheatreAttachmentClick?.(
                                attachmentIndex
                              )}
                              testId="journal-feed-postcard-mediacard"
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    "-"
                  )}
                </div>
              );
            } else {
              const previewAttachments = readFromObject
                ? _.map(
                    _.get(studentRoutineData, field.id),
                    attachment => attachment.value
                  )
                : _.find(fieldValues, { uid: field.id })?.resolvedMinimalTree
                    ?.attachmentGroups;
              return (
                <>
                  {" "}
                  <div className={labelClass}>
                    {field?.label ?? t("common:attachment")}
                  </div>
                  {_.size(previewAttachments) ? (
                    <PreviewAttachments
                      attachments={previewAttachments}
                      readValue={!readFromObject}
                    />
                  ) : (
                    "-"
                  )}
                </>
              );
            }
          } else {
            const valueObject = _.find(fieldValues, { uid: field.id });
            const isSectionHeader =
              field.type === FIELD_TYPE_ENUM.SECTION_HEADER;
            return (
              <div
                className={classes.fieldContainer}
                style={
                  isSectionHeader && index !== 0
                    ? styles.sectionHeaderTopStyle
                    : BLANK_OBJECT
                }
              >
                <div className={isSectionHeader ? labelClassV2 : labelClass}>
                  {field.label ?? _.first(field.dataSets)?.title}
                </div>
                {!isSectionHeader ? (
                  Array.isArray(valueObject?.value?.value) ? (
                    <div className={valueClass}>
                      {_.map(
                        _.filter(_.first(field.dataSets).values, item =>
                          _.includes(valueObject?.value?.value, item.id)
                        ),
                        item => _.get(item, "value")
                      ).join(", ")}
                    </div>
                  ) : (
                    <div className={valueClass}>
                      {field.type === "SINGLE_SELECT" ? (
                        _.find(_.first(field.dataSets)?.values, {
                          id: valueObject?.value?.value,
                        })?.value ?? "-"
                      ) : field.type === "DROPDOWN" ? (
                        _.find(_.first(field.dataSets).values, {
                          id: valueObject?.value?.value,
                        })?.value ?? "-"
                      ) : (
                        <span className={classes.wrapText}>
                          {valueObject?.value?.value ?? "-"}
                        </span>
                      )}
                    </div>
                  )
                ) : null}
              </div>
            );
          }
        })}
      {readFromObject &&
        _.map(fieldsForPreview, (field, index) => {
          if (field.type === "MEAL") {
            const mealValues = _.filter(field.dataSets, item =>
              _.isEqual(item.type, "MEAL_MENU")
            );
            return (
              <div className={classes.fieldContainer}>
                <div className={labelClass}>
                  {field.label ??
                    _.find(mealValues, {
                      id: _.get(studentRoutineData, `${[field?.id]}`)?.value
                        ?.mealMenuTemplateId,
                    })?.title ??
                    "-"}
                </div>
                {getValueByDataKey(
                  _.get(studentRoutineData, `${[field?.id]}`)?.value?.options,
                  field.dataSets,
                  _.get(studentRoutineData, `${[field?.id]}`)?.value
                    ?.selectedMealDate,
                  _.find(mealValues, {
                    id: _.get(studentRoutineData, `${[field?.id]}`)?.value
                      ?.mealMenuTemplateId,
                  })?.startDate,
                  _.find(mealValues, {
                    id: _.get(studentRoutineData, `${[field?.id]}`)?.value
                      ?.mealMenuTemplateId,
                  })?.endDate
                )}
              </div>
            );
          }
          if (field.type === "TIME_RANGE" || field.type === "TIME") {
            return (
              <TimeRange
                field={field}
                studentRoutineData={studentRoutineData}
              />
            );
          } else if (field.type === "ATTACHMENT") {
            const attachmentValues = readFromObject
              ? _.map(
                  _.get(studentRoutineData, field.id),
                  attachment => attachment.value
                )
              : _.find(fieldValues, { uid: field.id })?.value;
            return (
              <>
                {" "}
                <div className={labelClass}>
                  {field.label ?? t("common:attachment")}
                </div>
                {_.size(attachmentValues) ? (
                  <PreviewAttachments
                    attachments={attachmentValues}
                    readValue={!readFromObject}
                  />
                ) : (
                  <span className={classes.emptyPlaceHolder}>-</span>
                )}
              </>
            );
          } else {
            const isSectionHeader =
              field.type === FIELD_TYPE_ENUM.SECTION_HEADER;
            return (
              <div className={classes.fieldContainer}>
                <div
                  style={
                    isSectionHeader && index !== 0
                      ? styles.sectionHeaderTopStyle
                      : BLANK_OBJECT
                  }
                  className={isSectionHeader ? labelClassV2 : labelClass}
                >
                  {field.label ?? _.first(field?.dataSets)?.title}
                </div>
                {!isSectionHeader ? (
                  Array.isArray(
                    _.get(studentRoutineData, `${[field?.id]}`)?.value
                  ) ? (
                    <div className={valueClass}>
                      {_.map(
                        _.filter(_.first(field.dataSets).values, item =>
                          _.includes(
                            _.get(studentRoutineData, `${[field?.id]}`)?.value,
                            item.id
                          )
                        ),
                        item => _.get(item, "value")
                      ).join(", ")}
                    </div>
                  ) : (
                    <div className={valueClass}>
                      {field.type === "SINGLE_SELECT" ? (
                        _.find(_.first(field.dataSets)?.values, {
                          id: _.get(studentRoutineData, `${[field?.id]}`)
                            ?.value,
                        })?.value
                      ) : field.type === "DROPDOWN" ? (
                        _.find(_.first(field?.dataSets)?.values, {
                          id: _.get(studentRoutineData, `${[field?.id]}`)
                            ?.value,
                        })?.value ?? "-"
                      ) : (
                        <span className={classes.wrapText}>
                          {_.get(studentRoutineData, `${[field?.id]}`)?.value ??
                            "-"}
                        </span>
                      )}
                    </div>
                  )
                ) : null}
              </div>
            );
          }
        })}
    </div>
  );
};

export default FieldValue;
